<template>
  <div>
    <transition name="slide-up">
      <div v-if="isVisible" class="host-settings-panel">
        <div class="panel-content">
          <!-- Close Button -->
          <b-button variant="link" class="close-btn" @click="closePanel">
            <XIcon size="1.5x" />
          </b-button>

          <b-tabs pills card fill>
            <b-tab title="Settings" active>
              <div class="tab-content-inner-settings">
                <b-row>
                  <b-button variant="link" class="d-flex align-items-center close-button" @click="emitClose">
                    <XIcon size="1.5x" class="mr-1" /> Close Stream
                  </b-button>
                </b-row>
                <hr />
                <b-row>
                  <b-button variant="link" class="d-flex align-items-center close-button" @click="$emit('toggle-mic')">
                     <component :is="isHostMuted ? 'MicOffIcon' : 'MicIcon'" size="1.5x" class="mr-1" />
                     {{ isHostMuted ? 'Unmute Yourself' : 'Mute Yourself' }}
                   </b-button>
                </b-row>
              </div>
            </b-tab>

            <b-tab title="Stream Info">
               <div class="tab-content-inner">
                <h5 class="mb-3">Participants</h5>
                <!-- Loading State -->
                <div v-if="isLoading" class="text-center my-3">
                   <b-spinner label="Loading..."></b-spinner>
                 </div>
                 <!-- Error State -->
                <b-alert v-else-if="fetchError" show variant="danger">
                   {{ fetchError }}
                 </b-alert>
                 <!-- Empty State -->
                <div v-else-if="!panelParticipants.length" class="text-muted">
                   Currently no participants found.
                 </div>
                 <!-- Participant List -->
                 <div v-else>
                   <div v-for="p in panelParticipants" :key="p.uuid" class="participant-row d-flex align-items-center justify-content-between mb-2">
                     <div class="d-flex align-items-center">
                       <b-avatar :src="p.avatar || FALLBACK_AVATAR" size="3rem" class="mr-2"></b-avatar>
                       <span class="participant-name">{{ p.name }}</span>
                       <!-- Add role badge if needed (e.g., based on p.isSpeaker) -->
                       <b-badge v-if="!p.isSpeaker" variant="info" class="ml-2">Listener</b-badge>
                     </div>
                     <div class="actions">
                       <!-- Mute/Unmute Button (Only for Speakers) -->
                       <b-button
                         v-if="p.isSpeaker === true"
                         size="sm"
                         :variant="p.isMuted ? 'outline-secondary' : 'outline-success'"
                         class="mr-1 action-btn mute-btn"
                         @click="handleToggleMute(p)"
                       >
                         <component :is="p.isMuted ? 'MicOffIcon' : 'MicIcon'" size="1x" class="button-icon" />
                         {{ p.isMuted ? 'Sesi Aç' : 'Sessize Al' }}
                       </b-button>
                       <!-- Kick Button (For Everyone except Host - though host is filtered out) -->
                       <b-button
                         variant="danger"
                         size="sm"
                         class="action-btn kick-btn"
                         @click="handleKick(p)"
                       >
                         At
                       </b-button>
                     </div>
                   </div>
                 </div>
               </div>
             </b-tab>

<!-- Hediye satışı kaldırıldı — "Incoming Gifts" sekmesi devre dışı
             <b-tab title="Incoming Gifts">
                <div class="tab-content-inner">
                  <h5 class="mb-3">Hediye Gönderenler</h5>
                  <div v-if="!giftSenders.length" class="text-muted">Henüz hediye gönderen yok.</div>
                  <div v-for="giftInfo in giftSenders" :key="giftInfo.userUuid + '-' + giftInfo.productCode" class="gift-sender-row d-flex align-items-center justify-content-between mb-2">
                    <div class="d-flex align-items-center">
                      <b-avatar :src="giftInfo.userImage || FALLBACK_AVATAR" size="3rem" class="mr-2"></b-avatar>
                      <span class="sender-name">{{ giftInfo.userName }}</span>
                    </div>
                    <div class="gift-details d-flex align-items-center">
                      <img :src="resolveGiftImagePath(giftInfo.image)" :alt="giftInfo.label" class="gift-icon mr-2" />
                      <b-badge variant="info">x{{ giftInfo.count || 1 }}</b-badge>
                    </div>
                  </div>
                </div>
              </b-tab>
             -->
          </b-tabs>
        </div>
      </div>
    </transition>

    <!-- Kick Confirmation Modal (Now outside the v-if and transition) -->
    <KickConfirmationModal
      :visible.sync="showKickConfirmModal"
      :participant="participantToKick"
      @confirm="confirmKick"
    />
  </div>
</template>

<script>
import { BTabs, BTab, BButton, BRow, BCol, BAvatar, BSpinner, BAlert, BBadge } from 'bootstrap-vue'
import { XIcon, MicOffIcon, MicIcon, HeartIcon } from 'vue-feather-icons'
import KickConfirmationModal from './KickConfirmationModal.vue'
import {
  getListOfVideoRoomParticipants,
  kickUserService,
  toggleRemoteMuteService
} from '@/services/VideoRoomService'
import { showNotify } from '@/helpers/helpers'
import { mapGetters } from 'vuex'

const FALLBACK_AVATAR = require('@/assets/images/auth/female-avatar.png');

export default {
  name: 'VideoRoomHostSettingsPanel',
  components: {
    BTabs,
    BTab,
    BButton,
    BRow,
    BCol,
    BAvatar,
    BSpinner,
    BAlert,
    BBadge,
    XIcon,
    MicOffIcon,
    MicIcon,
    HeartIcon,
    KickConfirmationModal,
  },
  props: {
    isHostMuted: {
      type: Boolean,
      default: false,
    },
    channelId: {
      type: [String, Number],
      required: true
    },
  },
  data() {
    return {
      isVisible: false,
      fetchedListeners: [],
      isLoading: false,
      fetchError: null,
      showKickConfirmModal: false,
      participantToKick: null,
    }
  },
  computed: {
    ...mapGetters('videoRoom', [
      'speakers',
      'mainHost',
      'giftSenders'
    ]),
    panelParticipants() {
      const hostId = this.mainHost ? this.mainHost.id : null;
      const hostUuid = this.mainHost ? this.mainHost.uuid : null;

      const currentSpeakers = Array.isArray(this.speakers) ? this.speakers : [];

      const processedSpeakers = currentSpeakers
        .filter(speaker => speaker.id !== hostId)
        .map(speaker => ({
          ...speaker,
          isSpeaker: true
        }));

      const speakerUuids = new Set(processedSpeakers.map(s => s.uuid));

      const processedListeners = this.fetchedListeners
        .filter(listener => listener.uuid !== hostUuid && !speakerUuids.has(listener.uuid))
        .map(listener => ({
           id: listener.userId,
           uuid: listener.uuid,
           name: listener.userName,
           avatar: listener.image || FALLBACK_AVATAR,
           isMuted: listener.voiceMuted,
           isSpeaker: false
        }));

      return [...processedSpeakers, ...processedListeners];
    }
  },
  watch: {
  },
  methods: {
    emitClose() {
      this.$emit('close-stream');
    },
    openPanel() {
      this.isVisible = true;
      this.fetchListeners();
      this.$emit('panel-opened');
    },
    closePanel() {
      this.isVisible = false;
      this.fetchedListeners = [];
      this.isLoading = false;
      this.fetchError = null;
    },
    async fetchListeners() {
      if (!this.channelId) {
        this.fetchError = 'Channel ID is missing. Cannot fetch participants.';
        console.error('Cannot fetch participants:', this.fetchError);
        return;
      }
      this.isLoading = true;
      this.fetchError = null;
      this.fetchedListeners = [];

      try {
        // Get the host's identifier (assuming agoraUid is needed as publisherId)
        const hostAgoraUid = this.mainHost ? this.mainHost.agoraUid : null;

        // Check if we have the necessary IDs before calling the service
        if (!this.channelId || !hostAgoraUid) {
          this.fetchError = 'Channel ID or Host UID is missing. Cannot fetch participants.';
          console.error(this.fetchError, { channelId: this.channelId, hostAgoraUid });
          return; // Exit if IDs are missing
        }

        // Call the service with channelId and host's agoraUid as publisherId
        // The service now returns the participant array directly
        const participantsList = await getListOfVideoRoomParticipants(this.channelId, hostAgoraUid); 

        // Check if the returned value is an array
        if (Array.isArray(participantsList)) {
          this.fetchedListeners = participantsList; 
          console.log('[HostSettingsPanel] Fetched listeners:', this.fetchedListeners);
        } else {
          // This case might indicate an error handled within the service returning []
          console.warn('[HostSettingsPanel] getListOfVideoRoomParticipants did not return a valid array.');
          this.fetchedListeners = [];
        }

      } catch (error) {
        console.error(' Error fetching participants:', error);
        this.fetchError = error.message || 'Failed to load participant list.';
      } finally {
        this.isLoading = false;
      }
    },
    async handleToggleMute(participant) {
      if (!participant || !participant.uuid) {
        console.error(' Invalid participant data for toggle mute:', participant);
        return;
      }
      if (!this.channelId) {
         console.error(' Cannot toggle mute: channelId is missing.');
         return;
      }

      const actionText = participant.isMuted ? 'Unmute' : 'Mute';
      
      try {
        const success = await toggleRemoteMuteService(this.channelId, participant.uuid);
        if (success) {
          showNotify(this.$toast, `${participant.name} için ${actionText} isteği gönderildi.`, 'success');
        } else {
          showNotify(this.$toast, `${actionText} isteği gönderilirken bir sorun oluştu (sunucu).`, 'warning');
        }
      } catch (error) {
        console.error(` Error toggling mute for ${participant.uuid}:`, error);
        showNotify(this.$toast, error.message || `${actionText} isteği gönderilirken bir hata oluştu.`, 'danger');
      }
    },
    handleKick(participant) {
      if (!participant || !participant.uuid) {
        console.error(' Invalid participant data for kick:', participant);
        return;
      }
      this.participantToKick = participant;
      this.closePanel();
      this.$nextTick(() => {
          this.showKickConfirmModal = true;
      });
    },
    async confirmKick(participantId) {
      const participant = this.participantToKick; 
      if (!participant || participant.id !== participantId || !participant.uuid) {
        console.error(' Mismatch or missing participant data during kick confirmation:', participantId, this.participantToKick);
        this.resetKickModalState();
        return;
      }

      try {
          const success = await kickUserService(participant.uuid);
          if (success) {
            this.fetchedListeners = this.fetchedListeners.filter(l => l.uuid !== participant.uuid);
          } else {
            console.warn(` Kick service call failed for user: ${participant.uuid}`);
          }
      } catch (error) {
          console.error(` Error kicking user ${participant.uuid}:`, error);
      } finally {
          this.resetKickModalState();
      }
    },
    resetKickModalState() {
        this.showKickConfirmModal = false;
        this.participantToKick = null;
    },
    resolveGiftImagePath(imageName) {
      if (!imageName) return '';
      try {
        return require(`@/assets/images/gifts/${imageName}`);
      } catch (e) {
        console.warn(` Could not resolve gift image: ${imageName}`);
        return require('@/assets/images/gifts/star.svg');
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.host-settings-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 1050;
  background-color: rgba(40, 40, 55, 0.95);
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.3);
  padding: 15px;
  padding-bottom: 50px;
  padding-top: 30px;
  color: white;
}

.panel-content {
  position: relative;
  max-height: 70vh;
  overflow-y: auto;
}

.close-btn {
  position: absolute;
  top: -10px;
  right: 5px;
  color: #ccc;
  padding: 5px;
  line-height: 1;

  &:hover {
    color: white;
  }
}

$primary-color: #a644fb;
$inactive-tab-bg: transparent;
$active-tab-bg: $primary-color;
$tab-text-color: #ccc;
$active-tab-text-color: white;

::v-deep .nav-pills {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50px;
  padding: 5px;
  margin-bottom: 15px;

  .nav-link {
    color: $tab-text-color;
    background-color: $inactive-tab-bg;
    border-radius: 50px;
    font-weight: 500;
    border: none;
    padding: 8px 15px;
    transition: background-color 0.3s ease, color 0.3s ease;

    &.active {
      background-color: $active-tab-bg;
      color: $active-tab-text-color;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }

    &:not(.active):hover {
      color: white;
    }
  }

  .card {
    background-color: transparent;
    border: none;
  }
}

.close-button {
  color: #ccc;
  padding: 5px;
  line-height: 1;
  font-size: 1.2rem;
  font-weight: 500;
  text-decoration: none;
  border: none;
}

.tab-content-inner {
  padding: 10px;
  color: #eee;
  max-height: 40vh;
  overflow-y: auto;

  p {
    margin-bottom: 1rem;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  hr {
    border-color: rgba(255, 255, 255, 0.2);
  }

  .row {
    align-items: center;
    min-height: 40px;
  }
}

.tab-content-inner-settings {
  padding: 10px;
  color: #eee;

  p {
    margin-bottom: 1rem;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  hr {
    border-color: rgba(255, 255, 255, 0.2);
  }

  .row {
    align-items: center;
    min-height: 40px;
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.4s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
}

.slide-up-enter-to,
.slide-up-leave {
  transform: translateY(0);
}

.participant-row,
.gift-sender-row {
  background-color: rgba(255, 255, 255, 0.05);
  padding: 8px 12px;
  border-radius: 8px;
}

.participant-name, .sender-name {
  font-weight: 500;
}

.actions .action-btn {
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  min-width: 70px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mute-btn {
}

.kick-btn {
  background-color: #dc3545;
  border-color: #dc3545;
}

.button-icon {
  margin-right: 4px;
}

.gift-badge {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 20px;
  padding: 5px 10px;
  color: white;
  display: inline-flex;
  align-items: center;
}

.gift-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.gift-label {
  font-weight: 500;
}

.participant-row {
  margin-bottom: 0.75rem;
}

// Media Query for Mobile Tab Header Adjustments
@media (max-width: 767.98px) {
  // Target the tab links within the pills navigation
  ::v-deep .nav-pills .nav-link {
    font-size: 13px; // Smaller font size for tabs
    padding: 0.5rem 0.6rem; // Reduced padding
    white-space: nowrap; // Prevent text wrapping within the tab
  }
}

// Media Query for Desktop layout adjustment (assuming sidebar width 300px)
@media (min-width: 992px) { // Adjust breakpoint if needed
  .host-settings-panel {
    left: 290px; // Use the value user set
    width: calc(100% - 290px); // Use the value user set
    // Optionally adjust border-radius if sidebar overlaps left corner
    // border-top-left-radius: 0; // Removed this line to keep the radius
  }
}
</style> 